import React, {
  useState,
  useCallback,
  useMemo,
  memo,
  useRef,
  useEffect,
} from "react";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { API, graphqlOperation } from "aws-amplify";
import {
  Grid,
  Typography,
  useTheme,
  LinearProgress,
  Tooltip,
  Button as MuiButton,
  Popper,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Warning as WarningIcon } from "@material-ui/icons";
import { useNotify, useRedirect, Button, Confirm } from "react-admin";
import { useTrackContext } from "../TrackContext";
import { RailcarCard } from "../RailcarCard";
import { DroppableTrack } from "../../../../components/tracks/DroppableTrack";
import {
  TrackDndProvider,
  useTrackDnd,
} from "../../../../components/tracks/TrackDndProvider";
import {
  getCarsForTrack,
  hasSelectedOnTrack as hasSelectedOnTrackHelper,
  getSortableIds,
  sortTrackCars,
  VIRTUAL_UNSPOTTED_TRACK,
} from "../../../../utils/trackListUtils";
import {
  persistTrackCars,
  withTimePulledHistory,
} from "../../../../utils/trackCarPersistence";
import { getAisleFullName } from "../../../../utils/locationUtils";
import { updateTrackCar } from "../../../../graphql/mutations";
import {
  InboundStatus,
  OutboundStatus,
  UNSPOTTED_TRACK_ID,
} from "../../../../utils/constants";
import { logError } from "../../../../utils/appUtils";
import { CancelIcon } from "../../../../components/common/iconography";

/**
 * Optimized Railcar List component to isolate track re-renders
 */
const TrackRailcarList = memo(
  ({
    cars,
    fullCarsOnTrack,
    trackLocationId,
    selectedIdsSet,
    activeDragItemId,
    accountFilterId,
    foundCarMap,
    canDelete,
    handleShipmentClick,
    deleteItem,
    compactMode,
    loadedAccountIds,
    selectionCount,
    onMouseEnter,
    onMouseLeave,
    readonly = false,
    isCustomer = false,
    customerAccountId = null,
  }) => {
    // Staff Level Optimization: Windowing
    // Tracks with 100+ cars kill DOM performance. We limit the initial render.
    const [limit, setLimit] = useState(40);
    const hasMore = cars.length > limit;
    const visibleCars = hasMore ? cars.slice(0, limit) : cars;

    // Staff Level Optimization: Memoize ID array to prevent @dnd-kit re-indexing lag.
    // Moved here to follow Rules of Hooks.
    const sortableIds = useMemo(() => getSortableIds(cars), [cars]);

    // STAFF OPTIMIZATION: Pre-calculate index map to avoid O(N^2) in the render loop
    const indexMap = useMemo(() => {
      const map = new Map();
      fullCarsOnTrack.forEach((car, i) => map.set(car.id, i));
      return map;
    }, [fullCarsOnTrack]);

    // STAFF OPTIMIZATION: Disable expensive features during active drag
    const isGlobalDragging = !!activeDragItemId;

    return (
      <SortableContext
        items={sortableIds}
        strategy={verticalListSortingStrategy}
      >
        {visibleCars.map((shipment) => {
          const isSelected = selectedIdsSet.has(shipment.id);
          const isDragging = activeDragItemId === shipment.id;
          const realIdx = indexMap.get(shipment.id) ?? -1;

          return (
            <RailcarCard
              key={shipment.id}
              shipment={shipment}
              index={realIdx}
              trackLocationId={trackLocationId}
              isSelected={isSelected}
              isAccountMatched={
                !!accountFilterId && shipment.accountId === accountFilterId
              }
              showSearchIcon={
                isDragging ? false : !!(foundCarMap && foundCarMap[shipment.id])
              }
              canDelete={isDragging ? false : canDelete(shipment)}
              onShipmentClick={
                isDragging || readonly ? () => {} : handleShipmentClick
              }
              onDelete={isDragging || readonly ? () => {} : deleteItem}
              compactMode={compactMode}
              loadedAccountIds={loadedAccountIds}
              selectionCount={selectionCount}
              isOverlay={false}
              // Optimization: Suppression of tooltips/shadows during drag
              isDraggingGlobal={isGlobalDragging}
              style={isDragging ? { opacity: 0.3 } : undefined}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              readonly={readonly}
              isCustomer={isCustomer}
              customerAccountId={customerAccountId}
            />
          );
        })}
        {hasMore && (
          <MuiButton
            fullWidth
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setLimit((prev) => prev + 100);
            }}
            style={{ marginTop: 8, opacity: 0.7 }}
          >
            Show More ({cars.length - limit} remaining)
          </MuiButton>
        )}
      </SortableContext>
    );
  },
);

TrackRailcarList.displayName = "TrackRailcarList";

const useStyles = makeStyles((theme) => ({
  trackContainer: {
    padding: theme.spacing(2),
  },
  trackHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: (props) => theme.spacing(props.compactMode ? 0.5 : 2),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    gap: theme.spacing(1),
  },
  locationTitle: {
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  track: {
    minHeight: 250, // Slightly taller for better hit area
    padding: (props) => theme.spacing(props.compactMode ? 0.5 : 1),
    backgroundColor: theme.palette.background.paper,
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    gap: (props) => theme.spacing(props.compactMode ? 0.25 : 0.5),
    border: "1px dashed",
    borderRadius: theme.spacing(1),
    borderColor: theme.palette.grey[400],
  },
}));

/**
 * Main TrackGridView component that provides the DND Context
 */
export const TrackGridView = () => {
  const {
    processedCarsMap,
    compactMode,
    accountFilter,
    selectedShipmentIds,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    loadedAccountIds,
    readonly,
    isCustomer,
    customerAccountId,
  } = useTrackContext();

  const accountFilterId = accountFilter?.id;

  const renderRailcarOverlay = useCallback(
    (item) => (
      <RailcarCard
        shipment={item}
        index={-1}
        trackLocationId={item.trackId}
        isSelected={selectedShipmentIds.includes(item.id)}
        isAccountMatched={
          !!accountFilterId && item.accountId === accountFilterId
        }
        showSearchIcon={false}
        isOverlay={true}
        canDelete={false}
        onShipmentClick={() => {}}
        onDelete={() => {}}
        compactMode={compactMode}
        loadedAccountIds={loadedAccountIds}
        selectionCount={selectedShipmentIds?.length || 0}
        isCustomer={isCustomer}
        customerAccountId={customerAccountId}
      />
    ),
    [
      selectedShipmentIds,
      accountFilterId,
      compactMode,
      loadedAccountIds,
      isCustomer,
      customerAccountId,
    ],
  );

  return (
    <TrackDndProvider
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      renderOverlay={renderRailcarOverlay}
      processedCarsMap={processedCarsMap}
      disabled={readonly}
    >
      <TrackGridContent />
    </TrackDndProvider>
  );
};

/**
 * Optimized Track Bucket component to isolate track header and list re-renders
 */
const TrackBucket = memo(
  ({
    track,
    gridProps,
    classes,
    carsOnTrack,
    fullCarsOnTrack,
    selectedIdsSet,
    activeDragItemId,
    accountFilterId,
    foundCarMap,
    canDelete,
    handleShipmentClick,
    deleteItem,
    compactMode,
    loadedAccountIds,
    selectionCount,
    staleTracks,
    handleClearClick,
    theme,
    onMouseEnter,
    onMouseLeave,
    readonly = false,
    isCustomer = false,
    customerAccountId = null,
  }) => {
    const trackLocation = track.locations?.items?.[0];
    const trackLocationId = trackLocation?.id;
    if (!trackLocationId) {
      return null;
    }

    const hasSelectedOnTrack = hasSelectedOnTrackHelper(
      carsOnTrack,
      selectedIdsSet,
    );

    const capacityUsed =
      typeof trackLocation?.capacityUsed === "number"
        ? trackLocation.capacityUsed
        : null;
    const isStale = staleTracks[trackLocationId];
    const trackLabel =
      trackLocationId === UNSPOTTED_TRACK_ID
        ? "Unspotted"
        : getAisleFullName(track);

    return (
      <Grid item {...gridProps}>
        <div className={classes.trackHeader}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            <Tooltip title={trackLabel} arrow>
              <Typography variant="subtitle2" className={classes.locationTitle}>
                {trackLabel}
              </Typography>
            </Tooltip>
            {isStale && (
              <Tooltip
                title={`Network Error: Could not refresh this track. Showing last known state. (${isStale})`}
              >
                <WarningIcon
                  style={{ color: theme.palette.warning.main, fontSize: 18 }}
                />
              </Tooltip>
            )}
          </div>
          {capacityUsed != null && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 100,
                maxWidth: 200,
              }}
            >
              <LinearProgress
                variant="determinate"
                value={capacityUsed}
                style={{ flex: 1, marginRight: 8 }}
              />
              <Typography variant="caption" style={{ whiteSpace: "nowrap" }}>
                {capacityUsed}%
              </Typography>
            </div>
          )}
          <Button
            label="Clear"
            size="small"
            color="default"
            onClick={handleClearClick}
            style={{
              visibility: hasSelectedOnTrack ? "visible" : "hidden",
            }}
          >
            <CancelIcon />
          </Button>
        </div>
        <DroppableTrack
          trackLocationId={trackLocationId}
          className={classes.track}
        >
          <TrackRailcarList
            cars={carsOnTrack}
            fullCarsOnTrack={fullCarsOnTrack}
            trackLocationId={trackLocationId}
            selectedIdsSet={selectedIdsSet}
            activeDragItemId={activeDragItemId}
            accountFilterId={accountFilterId}
            foundCarMap={foundCarMap}
            canDelete={canDelete}
            handleShipmentClick={handleShipmentClick}
            deleteItem={deleteItem}
            compactMode={compactMode}
            loadedAccountIds={loadedAccountIds}
            selectionCount={selectionCount}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            readonly={readonly}
            isCustomer={isCustomer}
            customerAccountId={customerAccountId}
          />
        </DroppableTrack>
      </Grid>
    );
  },
  (prev, next) => {
    // Staff Level Optimization: Only re-render if data specific to THIS track or global filters changed
    return (
      prev.track === next.track &&
      prev.gridProps === next.gridProps &&
      prev.carsOnTrack === next.carsOnTrack &&
      prev.fullCarsOnTrack === next.fullCarsOnTrack &&
      prev.selectedIdsSet === next.selectedIdsSet &&
      prev.activeDragItemId === next.activeDragItemId &&
      prev.accountFilterId === next.accountFilterId &&
      prev.foundCarMap === next.foundCarMap &&
      prev.staleTracks === next.staleTracks &&
      prev.selectionCount === next.selectionCount &&
      prev.compactMode === next.compactMode &&
      prev.theme === next.theme &&
      prev.classes === next.classes &&
      prev.onMouseEnter === next.onMouseEnter &&
      prev.onMouseLeave === next.onMouseLeave &&
      prev.readonly === next.readonly &&
      prev.isCustomer === next.isCustomer &&
      prev.customerAccountId === next.customerAccountId
    );
  },
);

/**
 * Helper to determine grid sizing based on preference
 */
const getGridProps = (size) => {
  switch (size) {
    case 4:
      // Standard: 1 per row (mobile), 2 per row (tablet), 3 per row (medium), 4 per row (large)
      return { xs: 12, sm: 6, md: 4, lg: 3 };
    case 6:
      // Dense: 2 per row (mobile), 3 per row (tablet), 4 per row (medium), 6 per row (large)
      return { xs: 6, sm: 4, md: 3, lg: 2 };
    case 8:
      // Ultra-Dense: 2 per row (mobile), 4 per row (tablet), 6 per row (medium), 8 per row (large)
      return {
        xs: 6,
        sm: 3,
        md: 2,
        lg: "auto",
        style: { flexBasis: "12.5%", maxWidth: "12.5%" },
      };
    default:
      return { xs: 12, sm: 6, md: 4, lg: 3 };
  }
};

/**
 * Inner component that consumes the DND state and renders the grid
 */
const TrackGridContent = () => {
  const theme = useTheme();
  const notify = useNotify();
  const redirect = useRedirect();
  const {
    loading,
    setLoading,
    processedCarsMap,
    setProcessedCarsMap,
    tracks,
    gridSize,
    compactMode,
    accountFilter,
    visibleCarsMap,
    staleTracks = {},
    foundCarMap,
    saveShipment,
    openShipment,
    selectedShipmentIds,
    setSelectedShipmentIds,
    loadedAccountIds,
    getTracksDebounced,
    readonly,
    isCustomer,
    customerAccountId,
  } = useTrackContext();

  const accountFilterId = accountFilter?.id;
  const { activeItem: activeDragItem } = useTrackDnd();
  const classes = useStyles({ compactMode });
  const displayCarsMap = visibleCarsMap || processedCarsMap;

  const INITIAL_TRACK_BATCH = 12;
  const [visibleTrackCount, setVisibleTrackCount] = useState(() =>
    Math.min(INITIAL_TRACK_BATCH, tracks.length || 0),
  );

  const prevTracksLengthRef = useRef(tracks.length);
  useEffect(() => {
    const total = tracks.length || 0;
    // ONLY reset batching if the track list actually grew or shrunk significantly,
    // or if we were at 0 and now have tracks. This prevents flickering during
    // real-time updates to track capacity.
    if (prevTracksLengthRef.current === 0 && total > 0) {
      setVisibleTrackCount(Math.min(INITIAL_TRACK_BATCH, total));
    }

    prevTracksLengthRef.current = total;

    if (total > visibleTrackCount) {
      const id = setTimeout(() => setVisibleTrackCount(total), 100);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [tracks, visibleTrackCount]);

  const [open, setOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState();
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState("");
  const [lastSelectedContext, setLastSelectedContext] = useState(null);

  // STAFF LEVEL OPTIMIZATION: Singleton Tooltip state
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [tooltipTitle, setTooltipTitle] = useState("");

  const handleMouseEnter = useCallback(
    (event, title) => {
      if (activeDragItem) return;
      setTooltipAnchor(event.currentTarget);
      setTooltipTitle(title);
    },
    [activeDragItem],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltipAnchor(null);
    setTooltipTitle("");
  }, []);

  const gridProps = useMemo(() => getGridProps(gridSize), [gridSize]);

  const canDelete = useCallback((trackListShipment) => {
    const status =
      trackListShipment?.inbound?.status ??
      trackListShipment?.outbound?.status;
    return (
      status === InboundStatus.RELEASED || status === OutboundStatus.RELEASED
    );
  }, []);

  const deleteItem = useCallback(
    (shipment) => {
      setOpen(true);
      setSelectedShipment(shipment);
      setDeleteConfirmMessage(
        `This will mark railcar ${shipment.railCarNumber} as collected and permanently remove it from Track Management. Proceed?`,
      );
    },
    [setOpen, setSelectedShipment, setDeleteConfirmMessage],
  );

  const handleDialogClose = () => {
    if (readonly) return;
    setOpen(false);
  };

  const handleDeleteItem = async () => {
    if (readonly) return;
    try {
      setLoading(true);

      const trackId = selectedShipment?.trackId;
      const carsOnTrack = processedCarsMap[trackId] || [];

      const idx = carsOnTrack.findIndex(
        (car) => car.id === selectedShipment.id,
      );

      if (idx === -1) {
        return;
      }

      const toDeleteShipment = { ...selectedShipment };
      const collectedDateTime = new Date().toISOString();
      // History is read here, before any write, so the read never sits
      // between taking the car off the board and marking it collected.
      const updatedToDeleteShipment = await withTimePulledHistory({
        id: toDeleteShipment.id,
        parentType: toDeleteShipment.parentType,
        isCollected: true,
        collectedDateTime,
      });

      const updatedCars = [...carsOnTrack];
      updatedCars.splice(idx, 1);
      const sortedCars = sortTrackCars(updatedCars);

      const updatedProcessedCarsMap = {
        ...processedCarsMap,
        [trackId]: sortedCars,
      };

      const originalProcessedCarsMap = processedCarsMap;

      setProcessedCarsMap(updatedProcessedCarsMap);

      try {
        if (toDeleteShipment.id) {
          await API.graphql(
            graphqlOperation(updateTrackCar, {
              input: {
                id: toDeleteShipment.id,
                active: "false",
                trackId: "",
                sortKey: "",
                eodTrackId:
                  toDeleteShipment.eodTrackId ??
                  toDeleteShipment.trackId ??
                  "",
                eodSortKey:
                  toDeleteShipment.eodSortKey ??
                  toDeleteShipment.sortKey ??
                  "",
              },
            }),
          );
        }

        await saveShipment(updatedToDeleteShipment);

        const persistResult = await persistTrackCars(
          [trackId],
          updatedProcessedCarsMap,
          notify,
          tracks,
        );

        if (!persistResult.success) {
          throw new Error(
            `Failed to persist track car updates: ${persistResult.failed.length} failures`,
          );
        }

        if (typeof getTracksDebounced === "function") {
          getTracksDebounced([trackId]);
        }
      } catch (apiError) {
        setProcessedCarsMap(originalProcessedCarsMap);
        throw apiError;
      }
    } catch (err) {
      logError(
        "inboundShipment:inboundTrackManagement:deleteItem",
        err,
        notify,
        redirect,
      );
    } finally {
      setLoading(false);
      setOpen(false);
      setSelectedShipment(null);
    }
  };

  const clearSelection = useCallback(() => {
    setSelectedShipmentIds([]);
    setLastSelectedContext(null);
  }, [setSelectedShipmentIds]);

  const lastSelectedContextRef = useRef(lastSelectedContext);
  const selectedShipmentIdsRef = useRef(selectedShipmentIds);
  const displayCarsMapRef = useRef(displayCarsMap);

  useEffect(() => {
    lastSelectedContextRef.current = lastSelectedContext;
  }, [lastSelectedContext]);

  useEffect(() => {
    selectedShipmentIdsRef.current = selectedShipmentIds;
  }, [selectedShipmentIds]);

  useEffect(() => {
    displayCarsMapRef.current = displayCarsMap;
  }, [displayCarsMap]);

  const handleShipmentClick = useCallback(
    (event, shipment, trackLocationId) => {
      event.stopPropagation();
      const id = shipment.id;

      const carsOnTrack = displayCarsMapRef.current[trackLocationId] || [];
      const visibleIdx = carsOnTrack.findIndex((c) => c.id === id);

      if (event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();

        const sameTrackAnchor =
          lastSelectedContextRef.current &&
          lastSelectedContextRef.current.trackLocationId === trackLocationId;

        // STAFF LEVEL OPTIMIZATION: Disable cross-track selection
        if (!sameTrackAnchor && lastSelectedContextRef.current) {
          // If trying to shift-click on a different track, start a new selection on that track
          setSelectedShipmentIds([id]);
          setLastSelectedContext({ trackLocationId, index: visibleIdx });
          return;
        }

        const anchorIndex = sameTrackAnchor
          ? lastSelectedContextRef.current.index
          : visibleIdx;

        const start = Math.min(anchorIndex, visibleIdx);
        const end = Math.max(anchorIndex, visibleIdx);
        const idsInRange = carsOnTrack
          .slice(start, end + 1)
          .map((car) => car.id);

        setSelectedShipmentIds(idsInRange);

        if (!sameTrackAnchor) {
          setLastSelectedContext({ trackLocationId, index: visibleIdx });
        }
        return;
      }

      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();

        // STAFF LEVEL OPTIMIZATION: Disable cross-track selection
        const sameTrack =
          !lastSelectedContextRef.current ||
          lastSelectedContextRef.current.trackLocationId === trackLocationId;

        if (!sameTrack) {
          // If trying to multi-select on a different track, switch selection to the new track
          setSelectedShipmentIds([id]);
          setLastSelectedContext({ trackLocationId, index: visibleIdx });
          return;
        }

        const isSelected = selectedShipmentIdsRef.current.includes(id);
        const nextIds = isSelected
          ? selectedShipmentIdsRef.current.filter(
              (shipmentId) => shipmentId !== id,
            )
          : [...selectedShipmentIdsRef.current, id];

        setSelectedShipmentIds(nextIds);
        setLastSelectedContext({ trackLocationId, index: visibleIdx });
        return;
      }

      const isSelected = selectedShipmentIdsRef.current.includes(id);
      if (isSelected) {
        setSelectedShipmentIds(
          selectedShipmentIdsRef.current.filter(
            (shipmentId) => shipmentId !== id,
          ),
        );
        setLastSelectedContext(null);
      } else {
        if (readonly) return;
        setSelectedShipmentIds([]);
        setLastSelectedContext(null);
        openShipment(shipment);
      }
    },
    [setSelectedShipmentIds, setLastSelectedContext, openShipment, readonly],
  );

  const handleBackgroundClick = () => {
    if (readonly) return;
    if (selectedShipmentIds.length) {
      clearSelection();
    }
  };

  const handleClearClick = useCallback(
    (event) => {
      event.stopPropagation();
      if (readonly) return;
      clearSelection();
    },
    [clearSelection, readonly],
  );

  const selectedIdsSet = useMemo(
    () => new Set(selectedShipmentIds),
    // STAFF LEVEL OPTIMIZATION: Only recreate the Set when the IDs actually change.
    // During a drag, selectedShipmentIds is stable, so this Set will be referentially stable.
    [selectedShipmentIds],
  );

  const tracksToRender = useMemo(
    () => tracks.slice(0, visibleTrackCount),
    [tracks, visibleTrackCount],
  );

  // Props shared by every TrackBucket (unspotted bucket + real tracks); only
  // track/carsOnTrack/fullCarsOnTrack/key differ per bucket.
  const commonBucketProps = useMemo(
    () => ({
      gridProps,
      classes,
      selectedIdsSet,
      activeDragItemId: activeDragItem?.id,
      accountFilterId,
      foundCarMap,
      canDelete,
      handleShipmentClick,
      deleteItem,
      compactMode,
      loadedAccountIds,
      selectionCount: selectedShipmentIds?.length || 0,
      staleTracks,
      handleClearClick,
      theme,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      readonly,
      isCustomer,
      customerAccountId,
    }),
    [
      gridProps,
      classes,
      selectedIdsSet,
      activeDragItem?.id,
      accountFilterId,
      foundCarMap,
      canDelete,
      handleShipmentClick,
      deleteItem,
      compactMode,
      loadedAccountIds,
      selectedShipmentIds,
      staleTracks,
      handleClearClick,
      theme,
      handleMouseEnter,
      handleMouseLeave,
      readonly,
      isCustomer,
      customerAccountId,
    ],
  );

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={2}
        className={classes.trackContainer}
        onClick={handleBackgroundClick}
      >
        {/* Unspotted railcars bucket — always the top-left tile (manager only).
            Managers drag cars out of here to spot them onto a track, or back in
            to un-spot. Not rendered for the customer portal. */}
        {!isCustomer && (
          <TrackBucket
            key={VIRTUAL_UNSPOTTED_TRACK.id}
            track={VIRTUAL_UNSPOTTED_TRACK}
            carsOnTrack={getCarsForTrack(displayCarsMap, UNSPOTTED_TRACK_ID)}
            fullCarsOnTrack={getCarsForTrack(processedCarsMap, UNSPOTTED_TRACK_ID)}
            {...commonBucketProps}
          />
        )}

        {tracksToRender.map((track) => {
          const trackLocationId = track.locations?.items?.[0]?.id;
          if (!trackLocationId) return null;

          return (
            <TrackBucket
              key={track.id}
              track={track}
              carsOnTrack={getCarsForTrack(displayCarsMap, trackLocationId)}
              fullCarsOnTrack={getCarsForTrack(processedCarsMap, trackLocationId)}
              {...commonBucketProps}
            />
          );
        })}

        <Grid item xs={12}>
          <Confirm
            isOpen={open}
            loading={loading}
            title="Delete Confirmation"
            content={deleteConfirmMessage}
            onConfirm={handleDeleteItem}
            onClose={handleDialogClose}
          />
        </Grid>
      </Grid>

      {/* STAFF LEVEL OPTIMIZATION: Singleton Tooltip */}
      <Popper
        open={!!tooltipAnchor}
        anchorEl={tooltipAnchor}
        placement="top"
        style={{ zIndex: 1500, pointerEvents: "none" }}
        modifiers={{
          flip: { enabled: true },
          preventOverflow: { enabled: true, boundariesElement: "viewport" },
          arrow: { enabled: true },
        }}
      >
        <Paper
          elevation={4}
          style={{
            padding: "4px 8px",
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            color: "#fff",
            fontSize: "0.75rem",
            maxWidth: 300,
            borderRadius: 4,
          }}
        >
          {tooltipTitle}
        </Paper>
      </Popper>
    </>
  );
};
